/**
 * Fullscreen Chameleon Tail Canvas Controller
 * Features:
 * - Starts flush in the top-left corner but shifted down (baseY = 75) to prevent cropping
 * - Capped segment length on desktop so the tail curl tip is fully visible
 * - Idle state: fully open (uncoiled) and green
 * - Hover state: coils into a circle and turns Golden Orange
 * - Click/Tap Toggle: opens back up and turns Cyan/Blue
 * - Pop/Break Water Droplet Effect:
 *   - Touching/hovering a bubble triggers a splash particle burst.
 *   - The glass shell pops, showing only the text.
 *   - The text fades out and disappears.
 *   - The bubble slowly reforms and fades back in after 3 seconds.
 */
function initChameleonTailTimeline(sectionContainer) {
  const canvas = sectionContainer.querySelector(".chameleon-tail-canvas");
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  let isDestroyed = false;

  // Retrieve colors
  const colorGreenBase = canvas.dataset.color1 || "#3ba382";
  const colorGreenMid = canvas.dataset.color2 || "#4ab393";
  const colorOrangeStripe = canvas.dataset.color3 || "#a6663c";
  const milestoneTitleSize = parseInt(canvas.dataset.titleSize || "11");
  const milestoneTitleSizeMobile = parseInt(canvas.dataset.titleSizeMobile || "9");
  const bubbleSizeDesktop = parseInt(canvas.dataset.bubbleSizeDesktop || "54");
  const bubbleSizeMobile = parseInt(canvas.dataset.bubbleSizeMobile || "40");
  const tailTopDesktop = parseInt(canvas.dataset.tailTopDesktop || "75");
  const tailTopMobile = parseInt(canvas.dataset.tailTopMobile || "55");

  // Parse milestones from schema
  let rawMilestones = [];
  try {
    rawMilestones = JSON.parse(canvas.dataset.milestones || "[]");
  } catch (e) {
    console.error("Failed to parse milestones: ", e);
  }

  const N = 95; // Number of joints for a long, smooth tail

  // Pre-calculate target angles for unrolled and curled states
  const A_unrolled = [];
  const A_curled = [];

  let curlAngleAccumulator = 0;
  for (let i = 0; i <= N; i++) {
    // Unrolled (gentle horizontal sine wave sloping down-right)
    A_unrolled[i] = (i / N) * 0.2 + 0.45;

    // Curled (tight circular spiral)
    if (i < 24) {
      curlAngleAccumulator = Math.sin(i * 0.06) * 0.06;
    } else {
      let t = (i - 24) / (N - 24);
      let curvature = 0.068 + (t * 0.125);
      curlAngleAccumulator += curvature;
    }
    A_curled[i] = curlAngleAccumulator;
  }

  // Mouse tracking
  let mouseX = -9999;
  let mouseY = -9999;
  let isHovered = false;

  // Animation core transition states
  let currentCurl = 0.0;
  let targetCurl = 0.0;
  let colorProgress = 0.0;
  let targetColorProgress = 0.0;
  let isToggledOpen = false;

  // Tail base coordinates (dynamic centered)
  let baseX = 0; // Starts flush in the top-left corner
  let baseY = 75; // Shifted down slightly to prevent top-cropping
  let jointLength = 12;

  // Set up joints repulsion states
  const repulsionX = new Array(N + 1).fill(0);
  const repulsionY = new Array(N + 1).fill(0);
  const currentRepulsionX = new Array(N + 1).fill(0);
  const currentRepulsionY = new Array(N + 1).fill(0);

  // Splash particles array
  let splashParticles = [];

  // Weather drops array (Rain/drizzle droplets)
  const weatherDrops = [];
  const maxWeatherDrops = 24;
  for (let i = 0; i < maxWeatherDrops; i++) {
    weatherDrops.push({
      x: Math.random() * 1200,
      y: Math.random() * -600,
      vy: 5 + Math.random() * 4,
      vx: -0.5 + Math.random() * 1,
      length: 7 + Math.random() * 8,
      width: 1 + Math.random() * 0.5
    });
  }

  // Set up floating water bubble milestones
  const bubbles = rawMilestones.map((m, idx) => {
    const isMobile = window.innerWidth < 768;
    const initialRadius = isMobile ? bubbleSizeMobile : bubbleSizeDesktop;
    return {
      title: m.title || "MILESTONE",
      idx: idx,
      x: 0,
      y: 0,
      baseX: 0,
      baseY: 0,
      radius: initialRadius,
      currentRadius: initialRadius,
      driftTimeOffset: idx * 1.5,
      driftX: 0,
      driftY: 0,
      repelX: 0, 
      repelY: 0,
      collisionX: 0, 
      collisionY: 0,
      isBubbleHovered: false,
      
      // Popping states
      isPopped: false,
      popTimer: 0,
      shellOpacity: 1.0,
      textOpacity: 1.0
    };
  });

  // Handle resizing
  function resize() {
    const parent = canvas.closest(".chameleon-timeline-section");
    canvas.width = parent ? parent.clientWidth : window.innerWidth;
    canvas.height = parent ? parent.clientHeight : (window.innerWidth < 768 ? 480 : 700);

    baseX = 0; // Top-left corner
    baseY = window.innerWidth < 768 ? tailTopMobile : tailTopDesktop;
    
    // Scale length dynamically but cap it on desktop so it doesn't overshoot
    const baseLength = canvas.width / 95;
    jointLength = canvas.width >= 768 ? Math.min(13.8, baseLength) : baseLength;

    // Recalculate bubble base positions - dynamically centered in the middle of the section!
    const totalCount = bubbles.length || 1;
    const isMobile = canvas.width < 768;
    const spacing = isMobile ? Math.min(0.24, 0.85 / totalCount) : Math.min(0.14, 0.72 / totalCount);

    const activeRadius = isMobile ? bubbleSizeMobile : bubbleSizeDesktop;

    bubbles.forEach(b => {
      const percentX = 0.5 + (b.idx - (totalCount - 1) / 2) * spacing;
      const percentY = isMobile ? 0.54 + (Math.sin(b.idx * 2) * 0.07) : 0.5 + (Math.sin(b.idx * 2) * 0.08);

      b.radius = activeRadius;

      b.baseX = canvas.width * percentX;
      b.baseY = canvas.height * percentY;
      
      // Initialize x/y on first resize
      if (b.x === 0 && b.y === 0) {
        b.x = b.baseX;
        b.y = b.baseY;
        b.currentRadius = activeRadius;
      }
    });
  }
  window.addEventListener("resize", resize);
  resize();

  sectionContainer.addEventListener("shopify:section:unload", function() {
    isDestroyed = true;
    window.removeEventListener("resize", resize);
  });

  // Helper to parse hex to rgb
  const hexToRgb = (hex) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 46, g: 91, b: 59 };
  };

  const rgbGreenBase = hexToRgb(colorGreenBase);
  const rgbGreenMid = hexToRgb(colorGreenMid);
  const rgbOrangeStripe = hexToRgb(colorOrangeStripe);

  const activeColorBase = { r: 230, g: 126, b: 34 };  // State 1: Orange
  const activeColorMid = { r: 241, g: 196, b: 15 };   // State 1: Yellow

  const touchedColorBase = { r: 0, g: 229, b: 255 };  // State 2: Cyan
  const touchedColorMid = { r: 41, g: 121, b: 255 };  // State 2: Blue

  // Trigger splash particles burst
  function triggerSplash(px, py, color) {
    const numParticles = 12;
    for (let i = 0; i < numParticles; i++) {
      const angle = (i / numParticles) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const speed = 2.5 + Math.random() * 3.5;
      splashParticles.push({
        x: px,
        y: py,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 2 + Math.random() * 2.5,
        life: 25,
        maxLife: 25,
        color: color
      });
    }
  }

  // Helper to draw wrapped text inside bubbles
  // Helper to draw wrapped text inside bubbles
  function drawWrappedText(text, x, y, maxWidth, lineHeight, isTitle, colorHex, opacity) {
    ctx.save();
    const activeTitleSize = canvas.width < 768 ? milestoneTitleSizeMobile : milestoneTitleSize;
    ctx.font = isTitle ? "bold " + activeTitleSize + "px sans-serif" : "9px sans-serif";
    ctx.fillStyle = colorHex;
    ctx.globalAlpha = opacity;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const words = text.split(" ");
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + " " + word).width;
      if (width < maxWidth) {
        currentLine += " " + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);

    const startY = y - ((lines.length - 1) * lineHeight) / 2;
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], x, startY + i * lineHeight);
    }
    ctx.restore();
  }

  let time = 0;

  function animate() {
    if (isDestroyed) return;
    time += 0.025;

    // Smoothly transition unrolling curl state
    currentCurl += (targetCurl - currentCurl) * 0.065;

    // Smoothly transition color change (camouflage)
    colorProgress += (targetColorProgress - colorProgress) * 0.07;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate joint angles (breathing wave + coiling blend)
    const angle = [];
    for (let i = 0; i <= N; i++) {
      const breathingUnrolled = Math.sin(i * 0.06 - time) * 0.07;
      const breathingCurled = Math.sin(i * 0.06 + time * 0.6) * 0.04;

      const unrolledVal = (i / N) * 0.2 + 0.45 + breathingUnrolled;
      const curledVal = A_curled[i] + breathingCurled;

      angle[i] = (1 - currentCurl) * unrolledVal + currentCurl * curledVal;
    }

    const drawX = [baseX];
    const drawY = [baseY];

    for (let i = 1; i <= N; i++) {
      drawX[i] = drawX[i-1] + jointLength * Math.cos(angle[i-1]);
      drawY[i] = drawY[i-1] + jointLength * Math.sin(angle[i-1]);
    }

    // Dynamic scale thickness: 4.2% of screen width (e.g. 50px on 1200px screen)
    const getRadius = (index) => {
      const baseR = Math.max(38, canvas.width * 0.036);
      return baseR * (1 - (index / N) * 0.72);
    };

    // Helper to draw tail outline polygon
    function drawTailPolygon(offsetY, offsetX, fillStyle, strokeStyle, drawSpikes) {
      ctx.save();
      ctx.beginPath();
      
      const rStart = getRadius(0);
      ctx.moveTo(drawX[0] + offsetX + rStart * Math.cos(angle[0] + Math.PI / 2), drawY[0] + offsetY + rStart * Math.sin(angle[0] + Math.PI / 2));

      for (let i = 0; i <= N; i++) {
        const r = getRadius(i);
        const theta = angle[i] + Math.PI / 2;
        ctx.lineTo(drawX[i] + offsetX + r * Math.cos(theta), drawY[i] + offsetY + r * Math.sin(theta));
      }

      const rEnd = getRadius(N);
      ctx.arc(drawX[N] + offsetX, drawY[N] + offsetY, rEnd, angle[N] + Math.PI / 2, angle[N] - Math.PI / 2, true);

      for (let i = N; i >= 0; i--) {
        const r = getRadius(i);
        const theta = angle[i] - Math.PI / 2;
        ctx.lineTo(drawX[i] + offsetX + r * Math.cos(theta), drawY[i] + offsetY + r * Math.sin(theta));
      }

      ctx.closePath();
      ctx.fillStyle = fillStyle;
      ctx.fill();
      
      if (strokeStyle) {
        ctx.strokeStyle = strokeStyle;
        ctx.stroke();
      }

      // Spikes along outer curve (Under curve)
      if (drawSpikes) {
        for (let i = 2; i < N - 6; i += 3) {
          const r1 = getRadius(i);
          const theta1 = angle[i] - Math.PI / 2;
          const xBase1 = drawX[i] + offsetX + r1 * Math.cos(theta1);
          const yBase1 = drawY[i] + offsetY + r1 * Math.sin(theta1);

          const r2 = getRadius(i + 2);
          const theta2 = angle[i + 2] - Math.PI / 2;
          const xBase2 = drawX[i + 2] + offsetX + r2 * Math.cos(theta2);
          const yBase2 = drawY[i + 2] + offsetY + r2 * Math.sin(theta2);

          const spikeHeight = 6 * (1 - (i / N) * 0.6);
          const midR = getRadius(i + 1);
          const midTheta = angle[i + 1] - Math.PI / 2;
          const xTip = drawX[i + 1] + offsetX + (midR + spikeHeight) * Math.cos(midTheta);
          const yTip = drawY[i + 1] + offsetY + (midR + spikeHeight) * Math.sin(midTheta);

          ctx.beginPath();
          ctx.moveTo(xBase1, yBase1);
          ctx.lineTo(xTip, yTip);
          ctx.lineTo(xBase2, yBase2);
          ctx.closePath();
          ctx.fillStyle = fillStyle;
          ctx.fill();
          
          if (strokeStyle) {
            ctx.strokeStyle = strokeStyle;
            ctx.stroke();
          }
        }
      }
      ctx.restore();
    }

    // Interpolate colors based on colorProgress (transitions 0 -> 1 -> 2)
    let currentR1, currentG1, currentB1;
    let currentR2, currentG2, currentB2;

    if (colorProgress <= 1.0) {
      const t = colorProgress;
      currentR1 = Math.floor((1 - t) * rgbGreenBase.r + t * activeColorBase.r);
      currentG1 = Math.floor((1 - t) * rgbGreenBase.g + t * activeColorBase.g);
      currentB1 = Math.floor((1 - t) * rgbGreenBase.b + t * activeColorBase.b);

      currentR2 = Math.floor((1 - t) * rgbGreenMid.r + t * activeColorMid.r);
      currentG2 = Math.floor((1 - t) * rgbGreenMid.g + t * activeColorMid.g);
      currentB2 = Math.floor((1 - t) * rgbGreenMid.b + t * activeColorMid.b);
    } else {
      const t = colorProgress - 1.0;
      currentR1 = Math.floor((1 - t) * activeColorBase.r + t * touchedColorBase.r);
      currentG1 = Math.floor((1 - t) * activeColorBase.g + t * touchedColorBase.g);
      currentB1 = Math.floor((1 - t) * activeColorBase.b + t * touchedColorBase.b);

      currentR2 = Math.floor((1 - t) * activeColorMid.r + t * touchedColorMid.r);
      currentG2 = Math.floor((1 - t) * activeColorMid.g + t * touchedColorMid.g);
      currentB2 = Math.floor((1 - t) * activeColorMid.b + t * touchedColorMid.b);
    }

    const activeColor1 = `rgb(${currentR1}, ${currentG1}, ${currentB1})`;
    const activeColor2 = `rgb(${currentR2}, ${currentG2}, ${currentB2})`;

    // PASS A: Draw Soft Drop Shadow
    drawTailPolygon(14, 6, "rgba(0, 0, 0, 0.055)", null, true);

    // PASS B: Draw Solid Base Underlay
    const baseGrad = ctx.createLinearGradient(drawX[0], drawY[0], drawX[N], drawY[N]);
    baseGrad.addColorStop(0, activeColor1);
    baseGrad.addColorStop(0.5, activeColor2);
    baseGrad.addColorStop(1, activeColor2);
    drawTailPolygon(0, 0, baseGrad, `rgb(${Math.floor(currentR1*0.7)}, ${Math.floor(currentG1*0.7)}, ${Math.floor(currentB1*0.7)})`, true);

    // PASS C: Render Pebbly Scaled Skin Texture (Overlapping scales)
    for (let i = 0; i <= N; i++) {
      const r = getRadius(i);
      const perpAngle = angle[i] + Math.PI / 2;

      let isBand = false;
      if (i % 12 >= 8) {
        isBand = true;
      }

      const numScales = Math.max(3, Math.floor(r / 3.5));

      for (let s = 0; s < numScales; s++) {
        const t = numScales > 1 ? (s / (numScales - 1)) * 2 - 1 : 0;
        const offset = t * (r - 2.5);

        const scaleX = drawX[i] + offset * Math.cos(perpAngle);
        const scaleY = drawY[i] + offset * Math.sin(perpAngle);
        const scaleR = 3.2 * (1 - (i / N) * 0.58);

        ctx.beginPath();
        ctx.arc(scaleX, scaleY, scaleR, 0, Math.PI * 2);

        const colorNoise = Math.sin(i * 1.6 + s * 2.3) * 12;

        if (isBand) {
          const red = Math.min(255, Math.max(0, rgbOrangeStripe.r + colorNoise));
          const green = Math.min(255, Math.max(0, rgbOrangeStripe.g + colorNoise * 0.8));
          const blue = Math.min(255, Math.max(0, rgbOrangeStripe.b + colorNoise * 0.5));
          ctx.fillStyle = `rgb(${Math.floor(red)}, ${Math.floor(green)}, ${Math.floor(blue)})`;
        } else {
          const ratio = i / N;
          const targetR = (1 - ratio) * currentR1 + ratio * currentR2;
          const targetG = (1 - ratio) * currentG1 + ratio * currentG2;
          const targetB = (1 - ratio) * currentB1 + ratio * currentB2;

          const red = Math.min(255, Math.max(0, targetR + colorNoise * 0.6));
          const green = Math.min(255, Math.max(0, targetG + colorNoise));
          const blue = Math.min(255, Math.max(0, targetB + colorNoise * 0.8));
          ctx.fillStyle = `rgb(${Math.floor(red)}, ${Math.floor(green)}, ${Math.floor(blue)})`;
        }
        ctx.fill();

        // 3D Scale highlight
        ctx.beginPath();
        ctx.arc(scaleX - scaleR * 0.25, scaleY - scaleR * 0.25, scaleR * 0.25, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255, 255, 255, 0.22)";
        ctx.fill();
      }
    }

    // 4. Update and Draw Refractive Floating Water Bubbles
    bubbles.forEach(b => {
      b.driftX = Math.sin(time * 0.45 + b.driftTimeOffset) * 25;
      b.driftY = Math.cos(time * 0.35 + b.driftTimeOffset) * 25;

      let targetRadius = b.radius;

      // Current position before collision/repulsion physics
      let currentPosX = b.baseX + b.driftX;
      let currentPosY = b.baseY + b.driftY;

      // A. Collision check with tail joints (bounces out of the way)
      let maxOverlap = 0;
      let collisionPushX = 0;
      let collisionPushY = 0;

      // Only check collision if bubble is NOT fully popped
      if (!b.isPopped || b.popTimer > 30) {
        for (let i = 0; i <= N; i++) {
          const jdx = currentPosX - drawX[i];
          const jdy = currentPosY - drawY[i];
          const jdist = Math.sqrt(jdx*jdx + jdy*jdy);
          const minDist = getRadius(i) + b.currentRadius;

          if (jdist < minDist) {
            const overlap = minDist - jdist;
            if (overlap > maxOverlap) {
              maxOverlap = overlap;
              collisionPushX = (jdx / jdist) * overlap;
              collisionPushY = (jdy / jdist) * overlap;
            }
          }
        }
      }

      b.collisionX += (collisionPushX - b.collisionX) * 0.15;
      b.collisionY += (collisionPushY - b.collisionY) * 0.15;

      // B. "Dodge Cursor" and POPPING Math
      let targetRepelX = 0;
      let targetRepelY = 0;

      if (isHovered) {
        const checkX = currentPosX + b.collisionX + b.repelX;
        const checkY = currentPosY + b.collisionY + b.repelY;
        const bdx = checkX - mouseX;
        const bdy = checkY - mouseY;
        const bdist = Math.sqrt(bdx*bdx + bdy*bdy);

        if (!b.isPopped) {
          if (bdist < b.radius) {
            b.isBubbleHovered = true;
            targetRadius = b.radius * 1.15;
            
            // Pop the bubble!
            b.isPopped = true;
            b.popTimer = 300; // 5 seconds (2s text show, 1s fade, 2s silence/reappear)
            
            // Trigger splash droplet particles
            triggerSplash(checkX, checkY, activeColor1);
          } else {
            b.isBubbleHovered = false;
          }

          // Proximity dodge
          if (bdist < 130) {
            if (Math.abs(b.repelX) < 8 && Math.abs(b.repelY) < 8) {
              const angleAway = Math.atan2(bdy, bdx) + (Math.random() - 0.5) * 1.6;
              b.repelX = Math.cos(angleAway) * 150;
              b.repelY = Math.sin(angleAway) * 150;
            }
          }
        }
      } else {
        b.isBubbleHovered = false;
      }

      // C. Update Popped States and Opacities
      if (b.isPopped) {
        b.popTimer--;
        
        if (b.popTimer > 180) {
          // 1. Just popped: Shell popped, text visible
          b.shellOpacity = 0;
          b.textOpacity = 1.0;
        } else if (b.popTimer <= 180 && b.popTimer > 120) {
          // 2. Text Fading out (1 second)
          b.shellOpacity = 0;
          b.textOpacity = (b.popTimer - 120) / 60;
        } else if (b.popTimer <= 120 && b.popTimer > 30) {
          // 3. Silence / Disappeared (1.5 seconds)
          b.shellOpacity = 0;
          b.textOpacity = 0;
        } else if (b.popTimer <= 30 && b.popTimer > 0) {
          // 4. Bubble shell and text slowly reform/fade back in (0.5 seconds)
          const ratio = (30 - b.popTimer) / 30;
          b.shellOpacity = ratio;
          b.textOpacity = ratio;
        } else {
          // 5. Restore fully
          b.isPopped = false;
          b.shellOpacity = 1.0;
          b.textOpacity = 1.0;
        }
      } else {
        b.shellOpacity = 1.0;
        b.textOpacity = 1.0;
      }

      // Return repel offset back to 0
      b.currentRadius += (targetRadius - b.currentRadius) * 0.1;
      b.repelX += (0 - b.repelX) * 0.05;
      b.repelY += (0 - b.repelY) * 0.05;

      b.x = b.baseX + b.driftX + b.repelX + b.collisionX;
      b.y = b.baseY + b.driftY + b.repelY + b.collisionY;

      // Draw Glass Drop Specular Shadow
      if (b.shellOpacity > 0.02) {
        ctx.save();
        ctx.globalAlpha = b.shellOpacity;
        ctx.beginPath();
        ctx.arc(b.x + 4, b.y + 8, b.currentRadius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0, 0, 0, 0.04)";
        ctx.fill();
        ctx.restore();
      }

      // Draw Droplet Border Ring
      if (b.shellOpacity > 0.02) {
        ctx.save();
        ctx.globalAlpha = b.shellOpacity;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.currentRadius, 0, Math.PI * 2);
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = `rgba(${currentR1}, ${currentG1}, ${currentB1}, ${b.isBubbleHovered ? 0.8 : 0.45})`;
        ctx.stroke();
        ctx.restore();
      }

      // Create Radial Refraction/Glossy Fill
      if (b.shellOpacity > 0.02) {
        ctx.save();
        ctx.globalAlpha = b.shellOpacity;
        const bubbleGrad = ctx.createRadialGradient(
          b.x - b.currentRadius * 0.25,
          b.y - b.currentRadius * 0.25,
          b.currentRadius * 0.1,
          b.x,
          b.y,
          b.currentRadius
        );
        bubbleGrad.addColorStop(0, "rgba(255, 255, 255, 0.42)");
        bubbleGrad.addColorStop(0.3, "rgba(255, 255, 255, 0.08)");
        bubbleGrad.addColorStop(0.8, "rgba(0, 0, 0, 0.015)");
        bubbleGrad.addColorStop(1, b.isBubbleHovered ? "rgba(255, 255, 255, 0.25)" : "rgba(255, 255, 255, 0.14)");

        ctx.beginPath();
        ctx.arc(b.x, b.y, b.currentRadius, 0, Math.PI * 2);
        ctx.fillStyle = bubbleGrad;
        ctx.fill();

        // Top reflection highlight
        ctx.beginPath();
        ctx.ellipse(
          b.x - b.currentRadius * 0.3,
          b.y - b.currentRadius * 0.3,
          b.currentRadius * 0.2,
          b.currentRadius * 0.1,
          -Math.PI / 4,
          0,
          Math.PI * 2
        );
        ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
        ctx.fill();
        ctx.restore();
      }

      // Draw title and description wrapped inside droplet
      // Draw title wrapped inside droplet, centered exactly in the middle
      if (b.textOpacity > 0.02) {
        const maxTextWidth = b.currentRadius * 1.6;
        const textTitleColor = `rgb(${Math.floor(currentR1*0.75)}, ${Math.floor(currentG1*0.75)}, ${Math.floor(currentB1*0.75)})`;
        const activeTitleSize = canvas.width < 768 ? milestoneTitleSizeMobile : milestoneTitleSize;

        drawWrappedText(b.title, b.x, b.y, maxTextWidth, activeTitleSize + 2, true, textTitleColor, b.textOpacity);
      }
    });

    // 5. Update and Draw Splash Particles
    splashParticles.forEach((p, idx) => {
      p.x += p.vx;
      p.y += p.vy;
      p.life--;

      ctx.save();
      ctx.globalAlpha = p.life / p.maxLife;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius * (p.life / p.maxLife), 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.fill();
      ctx.restore();
    });

    // Clean up dead particles
    splashParticles = splashParticles.filter(p => p.life > 0);

    // 6. Update and Draw Weather Drops (Rain/drizzle droplets)
    ctx.save();
    ctx.strokeStyle = "rgba(174, 219, 235, 0.4)";
    ctx.lineCap = "round";

    weatherDrops.forEach(drop => {
      // Update position
      drop.x += drop.vx;
      drop.y += drop.vy;

      // Draw line
      ctx.lineWidth = drop.width;
      ctx.beginPath();
      ctx.moveTo(drop.x, drop.y);
      ctx.lineTo(drop.x + drop.vx * 1.5, drop.y + drop.length);
      ctx.stroke();

      // Check collision with the tail
      let hitTail = false;
      for (let i = 0; i <= N; i += 2) {
        const dx = drop.x - drawX[i];
        const dy = (drop.y + drop.length) - drawY[i];
        const dist = Math.sqrt(dx * dx + dy * dy);
        const limit = getRadius(i);

        if (dist < limit + 3) {
          hitTail = true;
          // Spawn 2 tiny splash particles matching active tail colors
          for (let j = 0; j < 2; j++) {
            splashParticles.push({
              x: drop.x,
              y: drawY[i] - 1,
              vx: -1.2 + Math.random() * 2.4,
              vy: -0.8 - Math.random() * 1.2,
              radius: 0.8 + Math.random() * 1.2,
              life: 8 + Math.floor(Math.random() * 6),
              maxLife: 14,
              color: `rgba(${currentR1}, ${currentG1}, ${currentB1}, 0.5)`
            });
          }
          break;
        }
      }

      // Reset drop if it hits the tail, falls off bottom, or goes off sides
      if (hitTail || drop.y > canvas.height || drop.x < 0 || drop.x > canvas.width) {
        drop.x = Math.random() * canvas.width;
        drop.y = Math.random() * -120 - 10;
        drop.vy = 5 + Math.random() * 4;
        drop.vx = -0.5 + Math.random() * 1;
      }
    });

    ctx.restore();

    requestAnimationFrame(animate);
  }

  // Mouse / Touch events (Starts uncoiled/open, coils on hover, tap/click toggles)
  canvas.addEventListener("mouseenter", () => {
    isHovered = true;
    if (!isToggledOpen) {
      targetCurl = 1.0; // Coil into circle
      targetColorProgress = 1.0; // Turn Golden Orange
    }
  });

  canvas.addEventListener("mousemove", (e) => {
    const rect = canvas.getBoundingClientRect();
    mouseX = (e.clientX - rect.left) * (canvas.width / rect.width);
    mouseY = (e.clientY - rect.top) * (canvas.height / rect.height);
    isHovered = true;
    
    if (!isToggledOpen) {
      targetCurl = 1.0;
      targetColorProgress = 1.0;
    } else {
      targetCurl = 0.0;
      targetColorProgress = 2.0; // Turn Cyan/Blue
    }
  });

  canvas.addEventListener("mouseleave", () => {
    isHovered = false;
    isToggledOpen = false; // Reset toggle
    targetCurl = 0.0; // Return to open state
    targetColorProgress = 0.0; // Return to calm Green
    mouseX = -9999;
    mouseY = -9999;
  });

  // Tap / click triggers the uncoil / color-shift toggle
  canvas.addEventListener("click", () => {
    isToggledOpen = !isToggledOpen;
    if (isToggledOpen) {
      targetCurl = 0.0; // Uncoils/Opens
      targetColorProgress = 2.0; // Turns Cyan/Blue
    } else {
      targetCurl = 1.0; // Coils/Closes
      targetColorProgress = 1.0; // Turns Orange
    }
  });

  // Touch triggers
  canvas.addEventListener("touchstart", (e) => {
    if (e.touches.length > 0) {
      const rect = canvas.getBoundingClientRect();
      mouseX = (e.touches[0].clientX - rect.left) * (canvas.width / rect.width);
      mouseY = (e.touches[0].clientY - rect.top) * (canvas.height / rect.height);
      isHovered = true;
      
      isToggledOpen = !isToggledOpen;
      if (isToggledOpen) {
        targetCurl = 0.0;
        targetColorProgress = 2.0;
      } else {
        targetCurl = 1.0;
        targetColorProgress = 1.0;
      }
    }
  });

  canvas.addEventListener("touchmove", (e) => {
    if (e.touches.length > 0) {
      const rect = canvas.getBoundingClientRect();
      mouseX = (e.touches[0].clientX - rect.left) * (canvas.width / rect.width);
      mouseY = (e.touches[0].clientY - rect.top) * (canvas.height / rect.height);
      isHovered = true;
    }
  });

  canvas.addEventListener("touchend", () => {
  });

  // Start loop
  animate();
}

// Initialize on load
if (document.readyState === "interactive" || document.readyState === "complete") {
  document.querySelectorAll(".chameleon-timeline-section-group").forEach(section => {
    initChameleonTailTimeline(section);
  });
} else {
  document.addEventListener("DOMContentLoaded", function() {
    document.querySelectorAll(".chameleon-timeline-section-group").forEach(section => {
      initChameleonTailTimeline(section);
    });
  });
}

// Re-initialize when Shopify Customizer updates the section
document.addEventListener("shopify:section:load", function(event) {
  const section = event.target.classList.contains("chameleon-timeline-section-group") 
    ? event.target 
    : event.target.querySelector(".chameleon-timeline-section-group");
  if (section) {
    initChameleonTailTimeline(section);
  }
});
